import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../utils/axiosInstance";

const initialState = {
	isLoading: true,
	error: null,
	message: null,
	ContactForm: null,
	ContactFormSubmission: null,
};

// ✅ Async Thunks
export const fetchContactUsFormData = createAsyncThunk(
	'/contact-management/form',
	async (_, { rejectWithValue }) => {
		try {
			const { data } = await axiosInstance.get("/admin/contact-management/contact-form");
			return data;
		} catch (error) {
			return rejectWithValue(error.response?.data || { message: "An error occurred" });
		}
	}
);

export const fetchContactFormSubmission = createAsyncThunk(
	'/contact-management/submission',
	async (_, { rejectWithValue }) => {
		try {
			const { data } = await axiosInstance.get("/admin/contact-management/contact-submission");
			return data;
		} catch (error) {
			return rejectWithValue(error.response?.data || { message: "An error occurred" });
		}
	}
);

export const updateContactUsForm = createAsyncThunk(
	'/contact-management/form/update',
	async (formData, { rejectWithValue }) => {
		try {
			const { data } = await axiosInstance.patch("/admin/contact-management/update-contact-form", formData);
			return data;
		} catch (error) {
			return rejectWithValue(error.response?.data || { message: "An error occurred" });
		}
	}
);

export const updateSubmission = createAsyncThunk(
	'/contact-management/submission/update',
	async ({ submissionsId, data: submissionData }, { rejectWithValue }) => {
		try {
			const { data } = await axiosInstance.patch(`/admin/contact-management/update-submission/${submissionsId}`, submissionData);
			return data;
		} catch (error) {
			return rejectWithValue(error.response?.data || { message: "An error occurred" });
		}
	}
);

// ✅ Slice
const contactManagement = createSlice({
	name: 'contact-management',
	initialState,
	reducers: {
		clearError: (state) => {
			state.error = null;
		},
		clearMessage: (state) => {
			state.message = null;
		}
	},
	extraReducers: (builder) => {
		builder
			// Fetch Form
			.addCase(fetchContactUsFormData.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(fetchContactUsFormData.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload?.message;
			})
			.addCase(fetchContactUsFormData.fulfilled, (state, action) => {
				state.isLoading = false;
				state.error = null;
				state.message = action.payload?.message;
				state.ContactForm = action.payload?.formFields;
			})

			// Fetch Submissions
			.addCase(fetchContactFormSubmission.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(fetchContactFormSubmission.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload?.message;
			})
			.addCase(fetchContactFormSubmission.fulfilled, (state, action) => {
				state.isLoading = false;
				state.error = null;
				state.message = action.payload?.message;
				state.ContactFormSubmission = action.payload?.submissions;
			})

			// Update Form
			.addCase(updateContactUsForm.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(updateContactUsForm.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload?.message;
			})
			.addCase(updateContactUsForm.fulfilled, (state, action) => {
				state.isLoading = false;
				state.error = null;
				state.message = action.payload?.message;
				state.ContactForm = action.payload?.formFields;
			})

			// Update Submission
			.addCase(updateSubmission.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(updateSubmission.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload?.message;
			})
			.addCase(updateSubmission.fulfilled, (state, action) => {
				state.isLoading = false;
				state.error = null;
				state.message = action.payload?.message;
				state.ContactFormSubmission = action.payload?.submissions;
			});
	},
});

export const { clearError, clearMessage } = contactManagement.actions;
export default contactManagement.reducer;
